import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { OnSubmitArgs, OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { useSelectorJs } from 'utils/customHooks';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { getOpParams } from 'utils/getOpParams';

import { AppDispatch, ImmutableRootState } from '../../../../../store/store';
import {
  searchVisitors,
  searchVisitorsToday,
  VisitorSearchState,
} from '../../../../../store/slices/visitorSearchSlice';
import { VisitorInvitationState } from '../../../../../store/slices/visitorInvitationSlice';

interface ModalFormProps {
  open: boolean;
  onOk: (data: VisitorListModalSubmitData) => void;
  onClose: () => void;
}

export interface VisitorListModalSubmitData {
  id?: number;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  mobilePhone?: string;
}

const VisitorListModal = ({ open, onOk, onClose }: ModalFormProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();

  const lastSearchParams = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorSearch', 'lastSearchParams']),
  ) as VisitorSearchState['lastSearchParams'];
  const globalLocationId = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocation', 'id']),
  ) as number;
  const invitationConfig = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorInvitation', 'invitationConfig']),
  ) as VisitorInvitationState['invitationConfig'];

  const [form] = OpForm.useForm();

  return (
    <OpModal
      open={open}
      title={t('visitors.visitorList.add')}
      okText={t('visitors.visitorList.save')}
      cancelText={t('visitors.visitorList.cancel')}
      width={720}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={() => {
        form
          .validateFields()
          .then((_values) => {
            form.submit();
            onClose();
          })
          .catch(() => {
            notificationService.show(
              'error',
              t('visitors.visitorList.validationError'),
            );
          });
      }}
      centered
    >
      <OpForm
        form={form}
        onSubmit={async ({
          values,
        }: OnSubmitArgs<VisitorListModalSubmitData>) => {
          const trimmedValues = Object.fromEntries(
            Object.entries(values).map(([key, value]) => [
              key,
              typeof value === 'string' ? value.trim() : value,
            ]),
          ) as VisitorListModalSubmitData;
          onOk(trimmedValues);
          dispatch(searchVisitorsToday({ orgId }));
          if (Object.keys(lastSearchParams).length !== 0) {
            dispatch(
              searchVisitors({
                ...lastSearchParams,
                orgId,
                locationId: lastSearchParams.locationId || globalLocationId!,
              }),
            );
          }
          form.resetFields();
        }}
        hasError={false}
        defaultButtons={false}
      >
        <OpForm.Input
          label={t('visitors.visitorList.firstName')}
          name="firstName"
          rules={[
            {
              required: true,
              message: t('visitors.visitorList.firstNameRequired'),
            },
          ]}
        />
        <OpForm.Input
          label={t('visitors.visitorList.lastName')}
          name="lastName"
          rules={[
            {
              required: true,
              message: t('visitors.visitorList.lastNameRequired'),
            },
          ]}
        />
        {invitationConfig?.email.included === 1 ? (
          <OpForm.Input
            label={t('visitors.visitorList.email')}
            name="email"
            rules={[
              {
                required: invitationConfig.email.required === 1,
                message: t('visitors.visitorList.emailRequired'),
              },
              {
                type: 'email',
                message: t('visitors.visitorList.emailInvalid'),
              },
            ]}
          />
        ) : undefined}
        {invitationConfig?.visitPhone.included === 1 ? (
          <OpForm.PhoneInput
            label={t('visitors.visitorList.mobilePhone')}
            name="mobilePhone"
            rules={[
              {
                required: invitationConfig.visitPhone.required === 1,
                message: t('visitors.visitorList.mobilePhoneRequired'),
              },
            ]}
          />
        ) : undefined}
      </OpForm>
    </OpModal>
  );
};

export default VisitorListModal;
